import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { finalize, from, Observable, Subject, takeUntil } from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { MapModelLike } from 'app/shared/models/map.model';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { MapsService } from 'app/shared/services/maps.service';
import { ToastService } from 'app/shared/services/toast.service';
import { NotesService } from '../../notes.service';
import { ToolMapType } from 'app/shared/models/tool-map-type';
import { TranslationService } from 'app/shared/services/translation.service';

interface MapFormLike {
  name: FormControl<string>;
}

@Component({
  selector: 'app-share-map-link-modal',
  templateUrl: 'share-map-link-modal.component.html',
  styleUrls: ['share-map-link-modal.component.scss'],
})
export class ShareMapLinkModalComponent implements OnInit, OnDestroy {
  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  formGroup: FormGroup<MapFormLike>;

  nameMaxLength = 50;

  link: string;

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private loadingController: LoadingController,
    private mainMapService: MainMapService,
    private mapsService: MapsService,
    private toastService: ToastService,
    private notesService: NotesService,
    public translationService: TranslationService // Adicionado para uso no template
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      name: this.formBuilder.control<string>(null, [
        Validators.required,
        Validators.maxLength(this.nameMaxLength),
      ]),
    });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onHidden() {
    this.link = null;
    this.formGroup.reset();

    this.mainMapService.disableToolMap(ToolMapType.ShareLink);
  }

  onBtnCancelClick() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnCopyClick() {
    from(navigator.clipboard.writeText(this.link))
      .pipe(takeUntil(this.lifecycle))
      .subscribe({
        next: () => {
          this.toastService.success(
            'Link copiado para a área de transferência'
          );
        },
        error: () => {
          this.toastService.error(
            this.translationService.getCurrentLanguage()[
              'link-copied-clipboard'
            ]
          );
        },
      });
  }

  onSubmit(e) {
    e?.preventDefault();
    if (this.loadingController.isShown()) {
      return;
    }

    let values: any = this.formGroup.getRawValue();
    values.basemapId = this.mainMapService.getBasemapId();
    values.viewPoint = this.mainMapService.getCurrentViewpoint();
    values.layers = this.mainMapService.getCurrentLayerVisibleList();
    values.notes = this.notesService.getNotes();

    this.loadingController.show();
    this.mapsService
      .createPublicMap(values)
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (model) => {
          this.toastService.success(
            this.translationService.getCurrentLanguage()[
              'map-link-generated-successfully'
            ]
          );

          const url = new URL(location.href);
          url.search = '';
          url.searchParams.set('mapId', model.id);

          this.link = url.toString();
          console.log(this.link);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-generate-link']
          );
        },
      });
  }
}
